import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatTimecode, type ShowcaseChapter } from "@shared/showcase";

/**
 * Where people watched, second by second: views as bars, rewatches as a line
 * on the SAME count axis (one axis; both are "viewers at this second").
 *
 * Colours: categorical slots 1 and 2 of the dataviz reference palette,
 * validated for both themes (blue/orange, CVD ΔE ≥ 24). Identity is never
 * colour alone: a legend plus a direct label on the rewatch peak.
 */

const SERIES = {
  light: { views: "#2a78d6", rewatches: "#eb6834" },
  dark: { views: "#3987e5", rewatches: "#d95926" },
};

/** Bucket per-second counts into at most `max` columns, keeping the peak. Exported for tests. */
export function bucket(
  values: number[],
  max: number
): { from: number; to: number; value: number }[] {
  if (values.length === 0) return [];
  const size = Math.max(1, Math.ceil(values.length / max));
  const out: { from: number; to: number; value: number }[] = [];
  for (let i = 0; i < values.length; i += size) {
    let peak = 0;
    for (let j = i; j < Math.min(values.length, i + size); j++)
      peak = Math.max(peak, values[j]);
    out.push({ from: i, to: Math.min(values.length, i + size), value: peak });
  }
  return out;
}

export default function WatchHeatmap({
  views,
  rewatches,
  chapters,
}: {
  views: number[];
  rewatches: number[];
  chapters: ShowcaseChapter[];
}) {
  const { t } = useTranslation("users");
  const theme = useTheme();
  const colors = SERIES[theme.palette.mode === "dark" ? "dark" : "light"];
  const [hover, setHover] = useState<number | null>(null);

  const W = 1000;
  const H = 180;
  const PAD_B = 22;
  const plotH = H - PAD_B - 8;
  const v = useMemo(() => bucket(views, 200), [views]);
  const r = useMemo(() => bucket(rewatches, 200), [rewatches]);
  const top = Math.max(1, ...v.map((b) => b.value), ...r.map((b) => b.value));
  const duration = views.length;
  const colW = v.length ? W / v.length : W;
  const y = (n: number) => 8 + plotH - (n / top) * plotH;

  const peak = r.reduce(
    (best, b, i) => (b.value > (r[best]?.value ?? 0) ? i : best),
    0
  );

  if (duration === 0 || top <= 0 || v.every((b) => b.value === 0)) {
    return (
      <Typography color="text.secondary" variant="body2">
        {t("watchHeatmap.noOne")}
      </Typography>
    );
  }

  const line = r
    .map((b, i) => `${i === 0 ? "M" : "L"}${(i + 0.5) * colW},${y(b.value)}`)
    .join(" ");
  const h = hover !== null ? { v: v[hover], r: r[hover] } : null;
  const hoverChapter =
    h &&
    chapters.reduce<ShowcaseChapter | null>(
      (hit, c) => (c.startSeconds <= h.v.from ? c : hit),
      null
    );

  return (
    <Box>
      <Stack direction="row" spacing={2.5} sx={{ mb: 1 }} aria-hidden="true">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "3px",
              bgcolor: colors.views,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t("watchHeatmap.viewers")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 14, height: 2, bgcolor: colors.rewatches }} />
          <Typography variant="caption" color="text.secondary">
            {t("watchHeatmap.cameBack")}
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ position: "relative" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={t("watchHeatmap.ariaViewersPerSecond", { peak: top })}
          onPointerLeave={() => setHover(null)}
          onPointerMove={(e) => {
            const rect = (
              e.currentTarget as SVGSVGElement
            ).getBoundingClientRect();
            const i = Math.floor(
              ((e.clientX - rect.left) / rect.width) * v.length
            );
            setHover(Math.max(0, Math.min(v.length - 1, i)));
          }}
          style={{ display: "block", overflow: "visible" }}
        >
          <line
            x1={0}
            x2={W}
            y1={8 + plotH}
            y2={8 + plotH}
            stroke={theme.palette.divider}
          />
          {v.map((b, i) => (
            <rect
              key={i}
              x={i * colW + 1}
              width={Math.max(1, colW - 2)}
              y={y(b.value)}
              height={Math.max(0, 8 + plotH - y(b.value))}
              rx={Math.min(3, colW / 3)}
              fill={colors.views}
              opacity={hover === null || hover === i ? 0.9 : 0.55}
            />
          ))}
          <path
            d={line}
            fill="none"
            stroke={colors.rewatches}
            strokeWidth={2}
            strokeLinejoin="round"
          />
          {r[peak]?.value > 0 && (
            <text
              x={Math.min(W - 4, (peak + 0.5) * colW)}
              y={Math.max(14, y(r[peak].value) - 8)}
              textAnchor={peak > v.length * 0.8 ? "end" : "middle"}
              fontSize={13}
              fill={theme.palette.text.secondary}
            >
              {t("watchHeatmap.mostRewatched", {
                time: formatTimecode(r[peak].from),
              })}
            </text>
          )}
          {chapters
            .filter((c) => c.startSeconds > 0 && c.startSeconds < duration)
            .map((c) => (
              <line
                key={c.startSeconds}
                x1={(c.startSeconds / duration) * W}
                x2={(c.startSeconds / duration) * W}
                y1={8}
                y2={8 + plotH}
                stroke={theme.palette.text.disabled}
                strokeDasharray="3 4"
              />
            ))}
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <text
              key={f}
              x={f * W}
              y={H - 4}
              fontSize={12}
              textAnchor={f === 0 ? "start" : f === 1 ? "end" : "middle"}
              fill={theme.palette.text.secondary}
            >
              {formatTimecode(f * duration)}
            </text>
          ))}
          {hover !== null && (
            <line
              x1={(hover + 0.5) * colW}
              x2={(hover + 0.5) * colW}
              y1={8}
              y2={8 + plotH}
              stroke={theme.palette.text.primary}
              strokeOpacity={0.35}
            />
          )}
        </svg>
        {h && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: `${((hover! + 0.5) / v.length) * 100}%`,
              transform: `translateX(${hover! > v.length * 0.7 ? "-105%" : "8px"})`,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              bgcolor: "background.paper",
              boxShadow: 3,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="fontWeightSemibold"
              display="block"
            >
              {formatTimecode(h.v.from)}
              {hoverChapter ? ` · ${hoverChapter.title}` : ""}
            </Typography>
            <Typography variant="caption" display="block">
              {t("watchHeatmap.viewersCount", { count: h.v.value })}
            </Typography>
            <Typography variant="caption" display="block">
              {t("watchHeatmap.rewatchedCount", { count: h.r?.value ?? 0 })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
